import React from 'react';
import { Link } from 'react-router-dom';
import { FiEdit, FiTrash2, FiCalendar, FiUser, FiAlertTriangle, FiClock } from 'react-icons/fi';
import riskService from '../../services/riskService';
import './RiskDetail.css';

const RiskDetail = ({ risk, onDelete, onEdit }) => {
  if (!risk) {
    return (
      <div className="risk-detail-container">
        <div className="risk-detail-loading">Loading risk details...</div>
      </div>
    );
  }

  const severity = risk.severity || riskService.getSeverityLevel(risk.risk_score);
  const daysSinceIdentified = riskService.getDaysSinceIdentified(risk.identified_date);
  const isOverdueForReview = riskService.isOverdueForReview(risk.review_date);

  return (
    <div className="risk-detail-container">
      {/* Header */}
      <div className="risk-detail-header">
        <div className="risk-detail-title-section">
          <h1 className="risk-detail-title">{risk.title}</h1>
          <div className="risk-detail-badges">
            <span className={`badge ${riskService.getRiskTypeColor(risk.risk_type)}`}>
              {riskService.getRiskTypeIcon(risk.risk_type)} {risk.risk_type}
            </span>
            <span className={`badge ${riskService.getStatusColor(risk.status)}`}>
              {riskService.getStatusIcon(risk.status)} {risk.status}
            </span>
            <span className={`badge ${riskService.getSeverityColor(severity)}`}>
              {severity} Severity
            </span>
          </div>
        </div>
        <div className="risk-detail-actions">
          {onEdit && (
            <button onClick={onEdit} className="btn-secondary">
              <FiEdit />
              Edit
            </button>
          )}
          {!onEdit && (
            <Link to={`/app/risks/${risk.id}/edit`} className="btn-secondary">
              <FiEdit />
              Edit
            </Link>
          )}
          {onDelete && (
            <button onClick={onDelete} className="btn-danger">
              <FiTrash2 />
              Delete
            </button>
          )}
        </div>
      </div>

      {/* Risk Score Card */}
      <div className="risk-score-card">
        <div className="risk-score-main">
          <div className="risk-score-icon">
            <FiAlertTriangle size={32} />
          </div>
          <div className="risk-score-content">
            <div className="risk-score-label">Risk Score</div>
            <div className="risk-score-value">{risk.risk_score}</div>
            <div className="risk-score-formula">
              {risk.probability} Probability × {risk.impact} Impact
            </div>
          </div>
        </div>
        <div className="risk-score-breakdown">
          <div className="risk-score-item">
            <span className="risk-score-item-label">Probability</span>
            <span className={`badge ${riskService.getProbabilityColor(risk.probability)}`}>
              {risk.probability}
            </span>
          </div>
          <div className="risk-score-item">
            <span className="risk-score-item-label">Impact</span>
            <span className={`badge ${riskService.getImpactColor(risk.impact)}`}>
              {risk.impact}
            </span>
          </div>
          <div className="risk-score-item">
            <span className="risk-score-item-label">Severity</span>
            <span className={`badge ${riskService.getSeverityBadgeColor(severity)}`}>
              {severity}
            </span>
          </div>
        </div>
      </div>

      {/* Main Content Grid */}
      <div className="risk-detail-grid">
        {/* Left Column */}
        <div className="risk-detail-main">
          {/* Description */}
          {risk.description && (
            <div className="risk-detail-section">
              <h3 className="risk-detail-section-title">Description</h3>
              <p className="risk-detail-text">{risk.description}</p>
            </div>
          )}

          {/* Mitigation Plan */}
          {risk.mitigation_plan && (
            <div className="risk-detail-section">
              <h3 className="risk-detail-section-title">🛡️ Mitigation Plan</h3>
              <p className="risk-detail-text">{risk.mitigation_plan}</p>
            </div>
          )}

          {/* Contingency Plan */}
          {risk.contingency_plan && (
            <div className="risk-detail-section">
              <h3 className="risk-detail-section-title">🚨 Contingency Plan</h3>
              <p className="risk-detail-text">{risk.contingency_plan}</p>
            </div>
          )}
        </div>

        {/* Right Column - Sidebar */}
        <div className="risk-detail-sidebar">
          {/* Project Info */}
          {risk.project && (
            <div className="risk-detail-card">
              <h4 className="risk-detail-card-title">Project</h4>
              <Link to={`/app/projects/${risk.project.id}`} className="risk-detail-project-link">
                <div className="risk-detail-project-name">{risk.project.name}</div>
                <div className="risk-detail-project-code">{risk.project.code}</div>
              </Link>
            </div>
          )}

          {/* Owner Info */}
          <div className="risk-detail-card">
            <h4 className="risk-detail-card-title">
              <FiUser /> Risk Owner
            </h4>
            {risk.owner ? (
              <div className="risk-detail-owner">
                <div className="risk-detail-owner-name">
                  {risk.owner.first_name} {risk.owner.last_name}
                </div>
                <div className="risk-detail-owner-email">{risk.owner.email}</div>
              </div>
            ) : (
              <div className="risk-detail-unassigned">Unassigned</div>
            )}
          </div>

          {/* Timeline */}
          <div className="risk-detail-card">
            <h4 className="risk-detail-card-title">
              <FiCalendar /> Timeline
            </h4>
            <div className="risk-detail-timeline">
              <div className="risk-detail-timeline-item">
                <span className="risk-detail-timeline-label">Identified</span>
                <span className="risk-detail-timeline-value">
                  {riskService.formatDate(risk.identified_date)}
                </span>
                {daysSinceIdentified !== null && (
                  <span className="risk-detail-timeline-days">
                    {daysSinceIdentified} days ago
                  </span>
                )}
              </div>
              {risk.review_date && (
                <div className="risk-detail-timeline-item">
                  <span className="risk-detail-timeline-label">Review Date</span>
                  <span className={`risk-detail-timeline-value ${isOverdueForReview ? 'overdue' : ''}`}>
                    {riskService.formatDate(risk.review_date)}
                    {isOverdueForReview && ' (Overdue)'}
                  </span>
                </div>
              )}
              {risk.closed_date && (
                <div className="risk-detail-timeline-item">
                  <span className="risk-detail-timeline-label">Closed</span>
                  <span className="risk-detail-timeline-value">
                    {riskService.formatDate(risk.closed_date)}
                  </span>
                </div>
              )}
            </div>
          </div>

          {/* Metadata */}
          <div className="risk-detail-card">
            <h4 className="risk-detail-card-title">
              <FiClock /> Metadata
            </h4>
            <div className="risk-detail-metadata">
              <div className="risk-detail-metadata-item">
                <span className="risk-detail-metadata-label">Created</span>
                <span className="risk-detail-metadata-value">
                  {riskService.formatDate(risk.created_at)}
                </span>
              </div>
              <div className="risk-detail-metadata-item">
                <span className="risk-detail-metadata-label">Last Updated</span>
                <span className="risk-detail-metadata-value">
                  {riskService.formatDate(risk.updated_at)}
                </span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RiskDetail;

